import React from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

/** Canonical production origin used to build absolute canonical + OG URLs. */
const SITE_URL = "https://withmaestro.org";

/** Default social-share card (1200x630) served from the app's public folder. */
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

/**
 * Centralized SEO meta for a route: title, description, canonical link, and
 * Open Graph + Twitter Card tags. Every marketing-relevant route should render
 * exactly one <Seo /> so per-page SERP listings and social previews stay
 * consistent and never drift apart. The canonical URL is derived from the
 * current pathname automatically.
 *
 * @param {object} props
 * @param {string} props.title - Full <title> for the route (brand included).
 * @param {string} props.description - Meta + OG/Twitter description (~150-160 chars).
 * @param {string} [props.image] - Absolute OG/Twitter image URL. Defaults to the brand card.
 * @param {"website"|"article"|"profile"} [props.type] - og:type. Defaults to "website".
 * @param {boolean} [props.noindex] - When true, emits robots noindex,nofollow.
 * @returns {JSX.Element}
 */
const Seo = ({
  title,
  description,
  image = DEFAULT_OG_IMAGE,
  type = "website",
  noindex = false,
}) => {
  const { pathname } = useLocation();
  const canonical = `${SITE_URL}${pathname}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex ? <meta name="robots" content="noindex,nofollow" /> : null}

      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Maestro" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </Helmet>
  );
};

export default Seo;
